import {
	Button,
	Inline,
	Stack,
	Divider,
	Grid,
	Col,
	Heading,
	Text,
} from "@sembark-travel/ui-base"
import {
	FileInputField,
	Form,
	PercentageInputField,
	PhoneInputField,
	PhoneNumberValidator,
	SelectField,
	SubmissionError,
	TextInputField,
	checkFileSize,
	checkFileTypes,
	isTruthy,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useState } from "react"
import * as Validator from "yup"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { useXHR } from "@sembark-travel/xhr"
import { TTenantBrand } from "./store"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { SelectBrandColorTheme, TBrandColorTheme } from "./BrandColorThemes"

export function AddTenantBrand({
	onSuccess,
	...props
}: Omit<React.ComponentProps<typeof NewTenantBrandForm>, "onSubmit"> & {
	onSuccess: (brand: TTenantBrand) => unknown
}) {
	const xhr = useXHR()
	return (
		<NewTenantBrandForm
			{...props}
			onSubmit={async (payload) => {
				const { data } = await xhr.post<{ data: TTenantBrand }>(
					`/tenant-brands`,
					payload
				)
				showSnackbar("Brand added successfully.")
				await Promise.resolve(onSuccess(data.data))
			}}
		/>
	)
}

type TTenantBrandFormData = {
	name: string
	short_name: string
	profile_image?: FileList
	color_theme?: TBrandColorTheme
	trip_destinations?: Array<TTripDestination>
	phone_numbers: Array<{
		country_code: string
		phone_number: string
		number: string
		is_primary?: boolean
	}>
	helpline_remarks?: string
	prefix_code?: string
	auto_lock_trip_past_days?: number
	min_margin_percentage?: number
}

export function NewTenantBrandForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	onSubmit: (data: FormData) => Promise<unknown>
	onCancel: () => unknown
	initialValues?: Partial<TTenantBrandFormData>
}) {
	const [initialValues] = useState<TTenantBrandFormData>(() => ({
		name: "",
		short_name: "",
		profile_image: undefined,
		color_theme: undefined,
		trip_destinations: [],
		...propInitialValues,
		phone_numbers: propInitialValues?.phone_numbers || [
			{
				country_code: "IN",
				phone_number: "+91",
				number: "",
			},
		],
	}))
	return (
		<Form<TTenantBrandFormData>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					name: Validator.string()
						.required("Please provide the registered name of the Brand")
						.max(100, "Please use 100 or less characters"),
					short_name: Validator.string()
						.required("Please provide the brand display name")
						.max(100, "Please use 100 or less characters"),
					phone_numbers: Validator.array()
						.of(PhoneNumberValidator("Phone Number", true))
						.nullable(),
					profile_image: Validator.mixed()
						.required("Please select a file")
						.nonNullable()
						.test(
							"type-is-correct",
							"Please select an png/jpeg image file",
							checkFileTypes(["image/png", "image/jpeg"])
						)
						.test(
							"size-not-big",
							"Please select a file that is less then 1 MB in size",
							checkFileSize(500)
						),

					helpline_remarks: Validator.string()
						.nullable()
						.max(100, "Please use 100 or less characters"),
					auto_lock_trip_past_days: Validator.number()
						.nullable()
						.min(0, "Days cannot be less than 0.")
						.max(90, "Days cannot exceed 90."),
					prefix_code: Validator.string()
						.transform((value) => (value === "" ? null : value))
						.nullable()
						.matches(/^[A-Z-]+$/, "Only alphabets and hyphen are allowed")
						.min(3, "Brand prefix code should be at least 3 characters.")
						.max(6, "Brand prefix code should not exceed 6 characters."),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				const data = new FormData()
				data.append("name", values.name)
				data.append("helpline_remarks", values.helpline_remarks || "")
				data.append("short_name", values.short_name)
				if (values.color_theme) {
					data.append("color_theme", values.color_theme.id)
				}
				data.append("prefix_code", values.prefix_code || "")
				const phone_numbers = values.phone_numbers
					?.filter((p) => String(p.number).trim())
					.map((p) => ({
						...p,
						is_primary: isTruthy(p.is_primary) ? 1 : 0,
					}))

				if (phone_numbers.length) {
					phone_numbers.forEach((number, index) => {
						data.append(
							`phone_numbers[${index}][country_code]`,
							number.country_code
						)
						data.append(
							`phone_numbers[${index}][phone_numbers]`,
							number.phone_number
						)
						data.append(
							`phone_numbers[${index}][number]`,
							String(number.number)
						)
					})
				}

				if (values.auto_lock_trip_past_days) {
					data.append(
						"auto_lock_trip_past_days",
						String(values.auto_lock_trip_past_days)
					)
				}

				values.trip_destinations?.forEach((d, index) => {
					data.append(`trip_destinations[${index}]`, String(d.id))
				})

				if (values.profile_image) {
					data.append("profile_image", values.profile_image as never)
				}

				if (values.min_margin_percentage) {
					data.append(
						"min_margin_percentage",
						values.min_margin_percentage as never
					)
				}
				await onSubmit(data)
			})}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="6">
						<Col sm={12} md={4}>
							<Stack>
								<Heading fontSize="md">Branding Details</Heading>
								<Text color="muted">
									Please provide brand name and it's registered namd with a
									Logo.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<TextInputField
									name="short_name"
									label="Short/Display/Brand Name"
									type="text"
									placeholder="e.g. ABC Travel"
								/>
								<TextInputField
									name="name"
									label="Official/Billing/Registered Name"
									type="text"
									placeholder="e.g. ABC Pvt Ltd"
								/>
								<FileInputField
									label="Select a Logo image"
									name="profile_image"
									accept="image/jpeg,image/png"
									help="Image should be 400x400 JPEG/PNG file with less than 500 KB in size."
								/>
								<SelectField
									select={SelectBrandColorTheme}
									label="Branding Color Theme"
									name="color_theme"
									fetchOnMount
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="6">
						<Col sm={12} md={4}>
							<Stack>
								<Heading fontSize="md">Destinations and Support</Heading>
								<Text color="muted">
									Associated Trip Destinations and general support contact
									details.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<SelectField
									name="trip_destinations"
									label="Trip Destinations"
									select={SelectTripDestination}
									multiple
									secondaryLabel="optional"
								/>
								<PhoneInputField
									name="phone_numbers"
									multi
									label="Support Contact Number(s)"
								/>
								<TextInputField
									name="helpline_remarks"
									label="Helpline Remarks"
									secondaryLabel="optional"
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="6">
						<Col sm={12} md={4}>
							<Stack>
								<Heading fontSize="md">Trip</Heading>
								<Text color="muted">Please provide Trip details</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<TextInputField
									name="auto_lock_trip_past_days"
									label="Auto Lock trip after past days"
									placeholder="Enter the no. of days"
									help="Trips will be locked the selected number of days after the trip date has passed."
									type="number"
									min={0}
									max={90}
									secondaryLabel={"optional"}
									size="sm"
									style={{ maxWidth: "300px" }}
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="6">
						<Col sm={12} md={4}>
							<Stack>
								<Heading fontSize="md">Brand Prefix Code</Heading>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<TextInputField
									name="prefix_code"
									label="Brand Prefix Code"
									secondaryLabel={"optional"}
									placeholder="Enter brand prefix code like SEM"
									help="The code will added to the Trip ID in all shareable documents, including emails, WhatsApp messages, PDFs, vouchers, invoices, and reports"
									size="sm"
									style={{ maxWidth: "300px" }}
									type="text"
									onChange={(e) =>
										form.change(
											"prefix_code" as never,
											e.currentTarget.value.toUpperCase() as never
										)
									}
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="6">
						<Col sm={12} md={4}>
							<Stack>
								<Heading fontSize="md">Markup Settings</Heading>
							</Stack>
						</Col>
						<Col>
							<PercentageInputField
								style={{ width: "300px" }}
								name="min_margin_percentage"
								label="Minimum Markup Percentage"
								secondaryLabel="optional"
								help="Quotes with a markup below this percentage cannot be saved."
							/>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="0">
						<Col sm={12} md={{ offset: 4, span: 8 }}>
							<Stack gap="4" paddingLeft={{ md: "6" }}>
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Creating..." : "Create Brand"}
									</Button>
									<Button onClick={() => onCancel()} level="tertiary">
										Cancel
									</Button>
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}
